import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@charphq/ui/components/base/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@charphq/ui/components/base/card";
import { Badge } from "@charphq/ui/components/base/badge";
import { Calendar, Clock, Thermometer, Droplets, Sun, Plus, Upload } from "lucide-react";
import { format } from "date-fns";
import {
  useGetApiCalibrations,
  type GetApiCalibrations200,
  type GetApiCalibrations200DataItem,
} from "@charphq/api-client";
import {
  CalibrationCreateDialog,
  CalibrationImportDialog,
} from "@charphq/ui/components/calibration/CalibrationDialogs";

type Calibration = GetApiCalibrations200DataItem;

function CalibrationCard({ calibration }: { calibration: Calibration }) {
  const dutCount = calibration.devices.filter((d) => d.role === "dut").length;
  const refCount = calibration.devices.filter((d) => d.role === "reference").length;
  const activePhases = calibration.phases.length;

  const metrics = [];
  if (calibration.phases.some((p) => p.includeTemperature)) metrics.push("Temperature");
  if (calibration.phases.some((p) => p.includeHumidity)) metrics.push("Humidity");
  if (calibration.phases.some((p) => p.includeLux)) metrics.push("Lux");

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg">{calibration.name}</CardTitle>
            {calibration.description && (
              <CardDescription className="mt-1">{calibration.description}</CardDescription>
            )}
          </div>
          <Badge variant={calibration.endAt ? "secondary" : "default"}>
            {calibration.endAt ? "Completed" : "Active"}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              <span>Started {format(new Date(calibration.startAt), "MMM d, yyyy")}</span>
            </div>
            {calibration.endAt && (
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                <span>Ended {format(new Date(calibration.endAt), "MMM d, yyyy")}</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1">
              <span className="font-medium">{dutCount}</span>
              <span className="text-muted-foreground">DUTs</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="font-medium">{refCount}</span>
              <span className="text-muted-foreground">References</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="font-medium">{activePhases}</span>
              <span className="text-muted-foreground">Phases</span>
            </div>
          </div>

          {metrics.length > 0 && (
            <div className="flex items-center gap-2">
              {metrics.includes("Temperature") && (
                <div className="flex items-center gap-1 text-temp">
                  <Thermometer className="h-4 w-4" />
                  <span className="text-sm">Temperature</span>
                </div>
              )}
              {metrics.includes("Humidity") && (
                <div className="flex items-center gap-1 text-humidity">
                  <Droplets className="h-4 w-4" />
                  <span className="text-sm">Humidity</span>
                </div>
              )}
              {metrics.includes("Lux") && (
                <div className="flex items-center gap-1 text-light">
                  <Sun className="h-4 w-4" />
                  <span className="text-sm">Lux</span>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function CalibrationsList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const {
    data: calibrationsResponse,
    isLoading,
    error,
  } = useGetApiCalibrations(undefined, {
    query: {
      staleTime: 30000, // 30 seconds
    },
  });

  const openRun = (calibrationId: string) => {
    queryClient.invalidateQueries();
    navigate({ to: "/calibrations/$calibrationId", params: { calibrationId } });
  };

  const calibrations = ((calibrationsResponse as GetApiCalibrations200 | undefined)?.data ||
    []) as Calibration[];

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-muted rounded animate-pulse" />
        <div className="grid gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-32 bg-muted rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-destructive mb-4">Failed to load calibrations</p>
        <Button onClick={() => window.location.reload()}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Calibrations</h1>
          <p className="text-muted-foreground">
            Manage device calibration runs and analyze performance
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setImportOpen(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Import run
          </Button>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Calibration
          </Button>
        </div>
      </div>

      <CalibrationCreateDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={openRun} />
      <CalibrationImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={openRun}
      />

      {calibrations && calibrations.length > 0 ? (
        <div className="grid gap-4">
          {calibrations.map((calibration) => (
            <Link
              key={calibration.id}
              to="/calibrations/$calibrationId"
              params={{ calibrationId: calibration.id }}
              className="block"
            >
              <CalibrationCard calibration={calibration} />
            </Link>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="mx-auto w-24 h-24 bg-muted rounded-full flex items-center justify-center mb-4">
            <Calendar className="h-12 w-12 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium mb-2">No calibrations yet</h3>
          <p className="text-muted-foreground mb-4">
            Create your first calibration run to get started
          </p>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Calibration
          </Button>
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute("/calibrations/")({
  component: CalibrationsList,
});
